import getMessagesForNamespaces from "@lib/i18n/getMessagesForNamespaces";
import { renderToString } from "react-dom/server";
import { useEffect } from "react";
import { InstantSearchSSRProvider, getServerState } from "react-instantsearch";
import { NextIntlClientProvider } from "next-intl";
import { Provider } from "react-redux";
import Cookies from "js-cookie";
import { wrapper } from "@lib/store";
import { useCurrentUser, useProductsRented } from "@hooks/use-current-user";
import useContentLoaderAnimation from "@hooks/use-content-loader-animation";
import {
  useCatalogNavigation,
  useClearCatalogLastScrollY,
} from "@hooks/use-catalog-navigation-hooks";
import CanonicalUrl from "@components/layout/CanonicalUrl";
import ListingHead from "@components/views/Listing/ListingHead";
import Catalog from "@components/views/Catalog";
import { getHub } from "@utils/call-api/hubs-call";
import isServer from "@utils/is-server";
import CustomLoader from "@components/core/CustomLoader";

const EmptyComponent = ({ children }) => <>{children}</>;

const Hub = ({ hub, context, page, url, withUser, serverState }) => {
  const user = useCurrentUser();
  const productsRented = useProductsRented();
  const catalogNavigation = useCatalogNavigation();
  const clearCatalogLastScrollY = useClearCatalogLastScrollY();
  const isFirstPage = page === "1" || !page;

  const Container = withUser ? EmptyComponent : InstantSearchSSRProvider;

  function canDisplayCatalog() {
    if (!isServer() && !user && Cookies.get("jwt-token")) return false;
    if (!isServer() && !productsRented && Cookies.get("jwt-token")) return false;

    return true;
  }

  const loaderShowDuration = 750;
  const { contentProps, loaderProps } = useContentLoaderAnimation(loaderShowDuration);

  useEffect(() => {
    setTimeout(() => {
      if (isServer()) return;

      window.scrollTo({ top: catalogNavigation?.lastScrollY, behavior: "instant" });
      clearCatalogLastScrollY();
    }, loaderShowDuration);
  }, []);

  return (
    <Container {...serverState}>
      <CanonicalUrl />
      <ListingHead
        page={page}
        isFirstPage={isFirstPage}
        seoTitle={hub.seo_title}
        seoDescription={hub.seo_description}
      />
      <div {...loaderProps}>
        <CustomLoader
          variant="Primary"
          style={{ width: "140px" }}
        />
      </div>
      <div {...contentProps}>
        {canDisplayCatalog() && (
          <Catalog
            context={context}
            hub={hub}
            seoContent={isFirstPage ? hub.seo_content : ""}
            url={url}
          />
        )}
      </div>
    </Container>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ req, query, params: { locale } }) => {
      let hub;

      try {
        hub = await getHub("braderie", locale);
      } catch (error) {
        return {
          notFound: true,
        };
      }

      const { context } = hub.data;

      const protocol = req.headers.referer?.split("://")[0] || "https";
      const url = `${protocol}://${req.headers.host}${req.url}`;

      const jwt = req.cookies["jwt-token"];

      const props = {
        hub: hub.data,
        context,
        page: query.page || "",
        url,
        headerBehaviors: {
          context,
          noHead: true,
        },
      };

      if (jwt) {
        return {
          props: {
            ...props,
            withUser: true,
            ...(await getMessagesForNamespaces(locale, ["common", "catalog", "feedbacks"])),
          },
        };
      }

      const { locale: msgLocale, messages } = await getMessagesForNamespaces(locale, [
        "common",
        "catalog",
        "feedbacks",
      ]);

      const serverState = await getServerState(
        <NextIntlClientProvider
          locale={msgLocale}
          messages={messages}
        >
          <Provider store={store}>
            <Hub
              hub={hub.data}
              context={context}
              page={query.page || ""}
              url={url}
              withUser={false}
            />
          </Provider>
        </NextIntlClientProvider>,
        {
          renderToString,
        },
      );

      return {
        props: {
          ...props,
          withUser: false,
          serverState,
          locale: msgLocale,
          messages,
        },
      };
    },
);

Hub.requireAuth = false;

export default Hub;
